import { UniqueUserId, UserClickFields, ViewContentFields } from '@xylabs/pixel'
import {
  GoogleAddToCartProperties,
  GoogleBeginCheckoutProperties,
  GoogleCartProperties,
  GoogleItem,
  GooglePurchaseProperties,
  GoogleValueProperties,
} from '@xylabs/react-pixel'
import { CartItemSlot } from '@xylabs/sdk-coin-js'

import { Cart, Funnel } from '../hooks'
import { getStoredQueryStringValue } from '../lib'
import { AddressInfoData } from '../models'
import { ContactInfoWrapper } from '../wrappers'
import { CoinUserEventHandlerBase } from './CoinUserEventHandlerBase'
import { CustomProperties } from './CustomProperties'
import { GoogleAddressInfoComplete, GoogleContactInfoComplete, GoogleEvents, GoogleViewContent } from './Google'
import { TestData } from './TestData'

export class GoogleEventHandler<T extends Record<string, unknown>> extends CoinUserEventHandlerBase<T> {
  async pageView(url: string) {
    await this.events.pageView(url)
  }

  async emailCollected(fields: { email: string }) {
    await this.events.emailCollected().send(this.buildCustomProperties(fields))
  }

  async accountCreated() {
    await this.events.accountCreated().send(this.buildCustomProperties())
  }

  async shareCompleted() {
    await this.events.shareCompleted().send(this.buildCustomProperties())
  }

  async buyticketsCompleted() {
    await this.events.buyticketsCompleted().send(this.buildCustomProperties())
  }

  async installCompleted() {
    await this.events.installCompleted().send(this.buildCustomProperties())
  }

  async funnelCompleted() {
    await this.events.funnelCompleted().send(this.buildCustomProperties())
  }

  async offerRequested(fields: { id: string }) {
    await this.events.offerRequested().send(this.buildCustomProperties(fields))
  }

  async feedbackScoreCollected(fields: { score: string | number }) {
    await this.events.feedbackScoreCollected().send(this.buildCustomProperties(fields))
  }

  async feedbackSourceCollected(fields: { source: string }) {
    await this.events.feedbackSourceCollected().send(this.buildCustomProperties(fields))
  }

  async goodFeedbackRequested() {
    await this.events.goodFeedbackRequested().send(this.buildCustomProperties())
  }

  async badFeedbackRequested() {
    await this.events.badFeedbackRequested().send(this.buildCustomProperties())
  }

  private events = new GoogleEvents<CustomProperties | GoogleCartProperties>()

  async contactInfoComplete() {
    const contactInfo = new ContactInfoWrapper().load()
    await this.events.contactInfoComplete().send(this.buildCartProperties<GoogleContactInfoComplete>(contactInfo.getSanitizedData()))
  }

  async addressInfoComplete(addressInfo?: AddressInfoData) {
    await this.events.addressInfoComplete().send(this.buildCartProperties<GoogleAddressInfoComplete>({ ...addressInfo }))
  }

  async paymentInfoComplete() {
    await this.events.paymentInfoComplete().send(this.buildCartProperties())
  }

  async addToCart() {
    await this.events.addToCart().send(this.buildCartProperties<GoogleAddToCartProperties>())
  }

  async purchaseCompleted() {
    await Promise.allSettled([
      this.events.purchase().send(this.buildCartProperties<GooglePurchaseProperties>()),
      this.events.transactionCompleted().send(this.buildCartProperties()),
    ])
  }

  async userClick(fields: UserClickFields) {
    await this.events.userClick().send(fields)
  }

  async initiateCheckout() {
    await this.events.beginCheckout().send(this.buildCartProperties<GoogleBeginCheckoutProperties>())
  }

  async purchaseAttempted() {
    await this.events.transactionAttempted().send(this.buildCartProperties())
  }

  async purchaseFailed() {
    await this.events.transactionFailed().send(this.buildCartProperties())
  }

  async upsellViewed() {
    await this.events.upsellViewed().send(this.buildCustomProperties())
  }

  async upsellUnavailable() {
    await this.events.upsellUnavailable().send(this.buildCustomProperties())
  }

  async upsellAccepted() {
    await this.events.upsellAccepted().send(this.buildCustomProperties())
  }

  async trialStarted() {
    await this.events.trialStarted().send(this.buildCustomProperties())
  }

  async funnelStarted() {
    await this.events.funnelStarted().send(this.buildCustomProperties())
  }

  async upsellSucceeded() {
    await Promise.allSettled([
      this.events.purchase().send(this.buildCartProperties<GooglePurchaseProperties>()),
      this.events.upsellSucceeded().send(this.buildCartProperties()),
    ])
  }

  async upsellFailed() {
    await this.events.upsellFailed().send(this.buildCustomProperties())
  }

  async testStarted() {
    await this.events.testStarted().send(this.buildCustomProperties())
  }

  async viewContent(fields: ViewContentFields) {
    await this.events.viewContent().send(this.buildCustomProperties<GoogleViewContent>(fields))
  }

  async customEvent(name: string, data: T) {
    await this.events.customEvent(name).send(this.buildCustomProperties(data))
  }

  buildCartProperties<T extends GoogleCartProperties>(data?: T): T | GoogleCartProperties {
    const items: GoogleItem[] = []

    const cart = Cart.get()
    for (const item of cart?.getItems() ?? []) {
      items.push({
        currency: 'USD',
        google_business_vertical: 'retail',
        id: item.planId,
        item_id: item.planId,
        item_name: item.title ?? item.planId,
        price: item.price,
        quantity: 1,
      })
    }

    return {
      ...data,
      ...this.buildValueProperties<T>(data),
      items,
    }
  }

  buildUpsellProperties<T extends GoogleCartProperties>(data?: T): T | GoogleCartProperties {
    const items: GoogleItem[] = []

    const item = Cart.get().getItemsBySlot(CartItemSlot.Upsell)
    if (item) {
      items.push({
        currency: 'USD',
        google_business_vertical: 'retail',
        id: item.planId,
        item_id: item.planId,
        item_name: item.title ?? item.planId,
        price: item.price,
        quantity: 1,
      })
    }

    return {
      ...data,
      currency: 'USD',
      items,
      value: item?.value ?? 0,
    }
  }

  buildValueProperties<T extends GoogleValueProperties>(data?: T): T | GoogleValueProperties {
    const cart = Cart.get()
    return {
      ...data,
      ...this.buildCustomProperties<T>(data),
      currency: 'USD',
      value: cart.getValue(),
    }
  }

  buildCustomProperties<T>(data?: T): T | CustomProperties {
    const cart = Cart.get()
    const coinId = new UniqueUserId().id
    const funnel = Funnel.get().data
    const testData = new TestData().data
    const tuneTransactionId = getStoredQueryStringValue('tune_transaction_id')

    return {
      ...data,
      ...funnel,
      coinId,
      itemSet: cart.getItemSet(),
      testData,
      tuneTransactionId: tuneTransactionId ?? undefined,
    }
  }
}
